/**
 * 관련법규 타입별 그룹화 및 원문자 항목 줄바꿈 유틸리티
 */
import React, { useMemo, useState } from 'react';
import {
  DEFAULT_LEGAL_BASIS_BADGE_HINT,
  DEFAULT_LEGAL_BASIS_BADGE_LABEL,
  formatLegalBasisChip,
  formatLegalBasisBody,
  summaryIncludesArticle,
} from '@safetyhub/shared';
import type { LegalBasis, LegalBasisSource } from '@safetyhub/shared';

// formatLegalBasisBody(+convertToCircled)·summaryIncludesArticle은 shared legal-basis-format.ts가
// 정본(서버 관계 법령 상세 블록도 같은 규칙). 기존 web 임포터 호환을 위해 re-export.
export { formatLegalBasisBody, summaryIncludesArticle };
import { Badge } from '@safetyhub/ui';
import { ExternalLinkIcon } from '../components/icons';

/** summary가 필수인 LegalBasis (그룹화 뷰 렌더링용) */
export type LegalBasisItem = Omit<LegalBasis, 'summary'> & { summary: string };

export interface LegalBasisWithStatus {
  law: LegalBasisItem;
  status?: 'unchanged' | 'added' | 'removed';
}

interface LegalGroup {
  type: string;
  items: LegalBasisWithStatus[];
}

/**
 * 법규를 타입별로 그룹화 (등장 순서 유지)
 */
export function groupLegalBasisByType(items: LegalBasisWithStatus[]): LegalGroup[] {
  const groups: LegalGroup[] = [];
  const typeMap = new Map<string, number>();
  for (const item of items) {
    const type = item.law.type;
    const idx = typeMap.get(type);
    if (idx !== undefined) {
      groups[idx].items.push(item);
    } else {
      typeMap.set(type, groups.length);
      groups.push({ type, items: [item] });
    }
  }
  return groups;
}

/**
 * 그룹 헤더 라벨 — 빈 type은 "기타"로 표시.
 */
function groupHeaderLabel(type: string): string {
  return type.trim() ? type : '기타';
}

const CIRCLED_RE = /[①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳]/;
const CIRCLED_PREFIX_RE = /^([①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳]\s?)/;
// 제N조, 제N조의N, 제N조(제목) 패턴
const ARTICLE_HEADER_RE = /^(제\d+조(?:의\d+)?(?:\([^)]+\))?)\s*/;

type SummaryPart =
  | { kind: 'header'; text: string }
  | { kind: 'circled'; prefix: string; body: string }
  | { kind: 'text'; text: string }
  | { kind: 'article-header'; header: string; body: string };

/**
 * summary를 조항 헤더/원문자 항목으로 분리하는 공통 헬퍼.
 * formatLegalSummary와 SummaryWithLink가 동일한 분리 로직을 공유합니다.
 */
function parseSummaryParts(summary: string): SummaryPart[] | null {
  if (CIRCLED_RE.test(summary)) {
    const parts = summary.split(/(?=[①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳])/).filter(p => p.trim());
    if (parts.length > 1) {
      return parts.map((part, i) => {
        const trimmed = part.trim();
        const isHeader = i === 0 && !CIRCLED_RE.test(trimmed.charAt(0));
        if (isHeader) return { kind: 'header', text: trimmed };
        const prefix = trimmed.match(CIRCLED_PREFIX_RE)?.[1] || '';
        return { kind: 'circled', prefix, body: trimmed.slice(prefix.length) };
      });
    }
  }
  const headerMatch = summary.match(ARTICLE_HEADER_RE);
  if (headerMatch && headerMatch[0].length < summary.length) {
    const body = summary.slice(headerMatch[0].length).trim();
    if (body) return [{ kind: 'article-header', header: headerMatch[1], body }];
  }
  return null;
}


/**
 * summary에서 조항 헤더와 원문자(①②③...) 항목을 분리하여 줄바꿈된 React 노드 반환
 *
 * 처리 순서:
 * 1. 원문자(①②③...) 있으면 → 원문자 기준 분리, 첫 파트가 조항 헤더면 bold
 * 2. 원문자 없지만 "제N조(...)" 패턴으로 시작 → 조항 헤더 + 본문 분리
 * 3. 해당 없으면 텍스트 그대로 반환
 */
export function formatLegalSummary(summary: string): React.ReactNode {
  if (!summary) return null;

  const parsed = parseSummaryParts(summary);
  if (parsed) {
    return (
      <>
        {parsed.map((part, i) => {
          if (part.kind === 'article-header') {
            return (
              <React.Fragment key={i}>
                <div className="font-medium">{part.header}</div>
                <div>{part.body}</div>
              </React.Fragment>
            );
          }
          if (part.kind === 'circled') {
            return (
              <div key={i} style={{ display: 'flex', gap: '0.25em' }}>
                <span style={{ flexShrink: 0 }}>{part.prefix}</span>
                <span>{part.body}</span>
              </div>
            );
          }
          return (
            <div key={i} className={part.kind === 'header' ? 'font-medium' : ''}>
              {part.text}
            </div>
          );
        })}
      </>
    );
  }

  return summary;
}

/**
 * 개별 법규의 article + summary를 중복 없이 렌더링
 * - summary가 article로 시작하면 article을 생략하고 summary만 표시
 * - 아니면 article을 별도 헤더로 표시
 */
function LawContent({
  law,
  url,
  articleClassName,
  summaryClassName,
  linkClassName,
}: {
  law: LegalBasisItem;
  url?: string;
  articleClassName: string;
  summaryClassName: string;
  linkClassName: string;
}) {
  const dedup = summaryIncludesArticle(law.article, law.summary);

  if (dedup) {
    // summary에 article이 포함됨 → summary만 렌더링 (URL은 summary 첫 줄에 적용)
    return (
      <div className={summaryClassName}>
        {url ? <SummaryWithLink summary={law.summary} url={url} linkClassName={linkClassName} /> : formatLegalSummary(law.summary)}
      </div>
    );
  }

  // article과 summary를 별도로 표시
  return (
    <>
      {law.article && (
        <div className={articleClassName}>
          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer"
              className={`hover:underline inline-flex items-center gap-1 ${linkClassName}`}
              onClick={(e) => e.stopPropagation()}>
              {law.article}
              <ExternalLinkIcon className="h-3 w-3 flex-none" />
            </a>
          ) : (
            <span>{law.article}</span>
          )}
        </div>
      )}
      {law.summary && (
        <div className={summaryClassName}>{formatLegalSummary(law.summary)}</div>
      )}
    </>
  );
}

/**
 * formatLegalSummary 결과의 첫 번째 줄(헤더)에 URL 링크를 적용
 */
function SummaryWithLink({ summary, url, linkClassName }: { summary: string; url: string; linkClassName: string }) {
  if (!summary) return null;

  const linkEl = (text: string) => (
    <a href={url} target="_blank" rel="noopener noreferrer"
      className={`hover:underline inline-flex items-center gap-1 ${linkClassName}`}
      onClick={(e) => e.stopPropagation()}>
      {text} <ExternalLinkIcon className="h-3 w-3 flex-none" />
    </a>
  );

  const parsed = parseSummaryParts(summary);
  if (parsed) {
    return (
      <>
        {parsed.map((part, i) => {
          if (part.kind === 'article-header') {
            return (
              <React.Fragment key={i}>
                <div className="font-medium">{linkEl(part.header)}</div>
                <div>{part.body}</div>
              </React.Fragment>
            );
          }
          if (part.kind === 'header') {
            return <div key={i} className="font-medium">{linkEl(part.text)}</div>;
          }
          if (part.kind === 'circled') {
            return (
              <div key={i} style={{ display: 'flex', gap: '0.25em' }}>
                <span style={{ flexShrink: 0 }}>{part.prefix}</span>
                <span>{part.body}</span>
              </div>
            );
          }
          return <div key={i}>{part.text}</div>;
        })}
      </>
    );
  }

  // fallback: 전체에 링크
  return linkEl(summary);
}

/**
 * 카드 스타일 그룹화 뷰 (점검 페이지 FindingDetail용)
 * 각 항목에 토글 기능 포함 (기본 접힘)
 */
export function GroupedLegalBasisCardView({
  items,
}: {
  items: LegalBasisWithStatus[];
}) {
  const sortedGroups = useMemo(() => {
    const groups = groupLegalBasisByType(items);
    return groups.map((group) => ({
      type: group.type,
      items: [...group.items].sort((a, b) => compareLegalBasisByArticle(a.law, b.law)),
    }));
  }, [items]);

  return (
    <div className="space-y-2">
      {sortedGroups.map((group) => (
        <div key={group.type} className="text-xs rounded border bg-blue-50 border-blue-100">
          <div className="font-medium text-blue-800 px-2 pt-2 pb-1">{groupHeaderLabel(group.type)}</div>
          <div className="space-y-1 px-2 pb-2">
            {group.items.map(({ law, status }, lIdx) => (
              <LegalBasisChip key={lIdx} law={law} status={status} omitType />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * 컴팩트 스타일 그룹화 뷰 (보고서 읽기 모드용)
 */
export function GroupedLegalBasisCompactView({
  legalBasis,
}: {
  legalBasis: LegalBasisItem[];
}) {
  const groups = groupLegalBasisByType(legalBasis.map(law => ({ law })));
  return (
    <div className="space-y-2">
      {groups.map((group) => (
        <div key={group.type}>
          <div className="font-medium text-ink-600 text-xs">{groupHeaderLabel(group.type)}</div>
          <div className="ml-3 space-y-1 mt-0.5">
            {group.items.map(({ law }, lIdx) => (
              <div key={lIdx} className="text-xs text-ink-500">
                <LawContent
                  law={law}
                  articleClassName="font-medium text-ink-600"
                  summaryClassName=""
                  linkClassName=""
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * 인쇄 모드 그룹화 뷰
 */
export function GroupedLegalBasisPrintView({
  legalBasis,
}: {
  legalBasis: LegalBasisItem[];
}) {
  const groups = groupLegalBasisByType(legalBasis.map(law => ({ law })));
  return (
    <>
      {groups.map((group) => (
        <div key={group.type} style={{ marginBottom: '4px' }}>
          <div style={{ fontWeight: 600 }}>{groupHeaderLabel(group.type)}</div>
          {group.items.map(({ law }, lIdx) => {
            const dedup = summaryIncludesArticle(law.article, law.summary);
            return (
              <div key={lIdx} style={{ marginLeft: '12px', marginTop: '2px' }}>
                {!dedup && law.article && <div style={{ fontWeight: 500 }}>{law.article}</div>}
                {law.summary && <div>{formatLegalSummary(law.summary)}</div>}
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

// ============================================================
// formatLegalBasisChip 및 헬퍼는 @safetyhub/shared로 이식(정본). 구 `formatSmsLegalBasisItem`은 호출부 0으로
// 2026-09-19 삭제. (web chip 라벨 / server refinedLegalBasisTexts 공용. 중복 구현 금지)
// ============================================================

const ARTICLE_NUMBER_RE = /제(\d+)조(?:의(\d+))?/;
const SUB_ARTICLE_WEIGHT = 0.01;

/**
 * 조번호("제42조", "제171조의2" 등)에서 정렬용 숫자 추출.
 * 가지번호(제N조의M)는 N + M*SUB_ARTICLE_WEIGHT 으로 안정 정렬.
 * 패턴 미매칭 시 Number.MAX_SAFE_INTEGER 반환 (맨 뒤 배치).
 */
export function extractArticleNumber(article?: string): number {
  if (!article) return Number.MAX_SAFE_INTEGER;
  const m = article.match(ARTICLE_NUMBER_RE);
  if (!m) return Number.MAX_SAFE_INTEGER;
  const main = parseInt(m[1], 10);
  const sub = m[2] ? parseInt(m[2], 10) * SUB_ARTICLE_WEIGHT : 0;
  return main + sub;
}

/**
 * 그룹 내 정렬용 비교 함수: similarity 내림차순 → 조번호 → paragraphNo(항) 순.
 * similarity 없는 legacy chip은 -Infinity 처리 → 항상 맨 아래.
 * 사용처: GroupedLegalBasisCardView, EditableLegalList
 */
export function compareLegalBasisByArticle(
  a: { article?: string; paragraphNo?: string; similarity?: number },
  b: { article?: string; paragraphNo?: string; similarity?: number },
): number {
  const simA = a.similarity ?? -Infinity;
  const simB = b.similarity ?? -Infinity;
  if (simA !== simB) return simB - simA;
  const diff = extractArticleNumber(a.article) - extractArticleNumber(b.article);
  if (diff !== 0) return diff;
  return (a.paragraphNo ?? '').localeCompare(b.paragraphNo ?? '', undefined, { numeric: true });
}

/** chip 클릭 시 모달/툴팁에 표시할 전체 본문 */
export function formatLegalBasisDetail(lb: { summary?: string }): string {
  return (lb.summary ?? '').trim();
}

// (formatLegalBasisBody/convertToCircled 정의는 @safetyhub/shared legal-basis-format.ts로 이식 —
// 파일 상단 re-export 참조. JSDoc·결정 기록 링크도 shared 쪽이 정본.)

/**
 * 관계 법규 chip 컴포넌트 — 기본 접힘, 클릭 시 본문 토글.
 *
 * - 라벨: formatLegalBasisChip 결과
 * - 본문: formatLegalBasisBody (호 매칭 시 mok 들여쓰기, 조 매칭 시 paragraphs fallback)
 * - 기본값: 접힘 (expanded = false)
 * - 선택: onRemove 핸들러 제공 시 우측에 삭제 버튼 표시
 * - 기본 근거(`source: 'default'` — 판정 채택 0건 폴백, 결정 2026-09-18 legal-basis-default-fallback):
 *   라벨 옆에 「기본 근거」 배지(툴팁 = 사유). 텍스트 surface의 ' [기본 근거]' 꼬리표(shared chip 기본값)
 *   대신 배지로 그리므로 chip 라벨은 `markDefault: false`로 만든다(배지+꼬리표 이중 표기 방지).
 */
export function LegalBasisChip({
  law,
  status = 'unchanged',
  onRemove,
  isRemoving = false,
  omitType = false,
}: {
  law: {
    type?: string;
    article?: string;
    paragraphNo?: string;
    title?: string;
    summary?: string;
    url?: string;
    paragraphs?: Array<{ paragraphNo: string; content: string }>;
    subItems?: Array<{ subItemNo: string; content: string | null }>;
    source?: LegalBasisSource;
  };
  status?: 'unchanged' | 'added' | 'removed';
  onRemove?: () => void;
  isRemoving?: boolean;
  omitType?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const label = formatLegalBasisChip(law, { omitType, markDefault: false });
  const body = formatLegalBasisBody(law).trim();
  const hasBody = body.length > 0;

  const isRemoved = status === 'removed';
  const isAdded = status === 'added';
  const isDefault = law.source === 'default';
  // 라벨 옆 배지 묶음 — 토글 버튼 유무(hasBody) 두 분기가 같은 것을 그린다.
  const labelBadges = (
    <>
      {isDefault && (
        <span title={DEFAULT_LEGAL_BASIS_BADGE_HINT} className="flex-shrink-0">
          <Badge tone="amber" size="sm">
            {DEFAULT_LEGAL_BASIS_BADGE_LABEL}
          </Badge>
        </span>
      )}
      {isAdded && (
        <Badge tone="emerald" size="sm" className="flex-shrink-0">
          +추가됨
        </Badge>
      )}
      {isRemoved && (
        <Badge tone="rose" size="sm" className="flex-shrink-0">
          -삭제됨
        </Badge>
      )}
    </>
  );

  const baseClasses = 'text-xs rounded border';
  const statusClasses = {
    unchanged: 'bg-blue-50 border-blue-100',
    added: 'bg-green-50 border-green-200',
    removed: 'bg-red-50 border-red-200 opacity-60',
  };

  const linkColorClass = isAdded ? 'text-green-500' : 'text-blue-500';
  const labelColorClass = isRemoved
    ? 'text-red-600 line-through'
    : isAdded
      ? 'text-green-800'
      : 'text-blue-800';
  const bodyColorClass = isRemoved
    ? 'text-red-500'
    : isAdded
      ? 'text-green-700'
      : 'text-blue-700';
  const bodyBorderClass = isAdded ? 'border-green-200' : 'border-blue-100';

  return (
    <div className={`${baseClasses} ${statusClasses[status]} ${isRemoving ? 'opacity-50' : ''}`}>
      <div className="flex items-start gap-1 p-2">
        {hasBody ? (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="flex-1 min-w-0 flex items-start gap-1 text-left hover:opacity-80"
            aria-expanded={expanded}
          >
            <span className={`flex-shrink-0 mt-0.5 ${linkColorClass}`}>
              {expanded ? (
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              ) : (
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              )}
            </span>
            <span className={`font-medium min-w-0 flex items-center gap-1 flex-wrap ${labelColorClass}`}>
              <span>{label}</span>
              {labelBadges}
            </span>
          </button>
        ) : (
          <div className="flex-1 min-w-0 flex items-start gap-1">
            <div className={`font-medium flex items-center gap-1 flex-wrap ${labelColorClass}`}>
              <span>{label}</span>
              {labelBadges}
            </div>
          </div>
        )}
        {law.url && (
          <a
            href={law.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            className={`flex-shrink-0 mt-0.5 hover:opacity-70 ${linkColorClass}`}
            aria-label="법령 원문 새 창 열기"
            title="법령 원문 새 창 열기"
          >
            <ExternalLinkIcon className="h-3.5 w-3.5" />
          </a>
        )}
        {onRemove && !isRemoved && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            disabled={isRemoving}
            className="text-red-400 hover:text-red-600 p-0.5 flex-shrink-0 disabled:opacity-50"
            title="삭제"
            aria-label="법규 삭제"
          >
            {isRemoving ? (
              <div className="animate-spin w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full" />
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        )}
      </div>
      {expanded && hasBody && (
        <div className={`px-2 pb-2 text-[11px] whitespace-pre-wrap leading-relaxed border-t pt-1.5 ${bodyBorderClass} ${bodyColorClass}`}>
          {body}
        </div>
      )}
    </div>
  );
}
